import { observer } from "mobx-react"
import { useEffect, useState } from "react"
import { hasVoiceBridge, voiceAPI } from "../electronAPIs/voice"
import type { CodeStore } from "../store/store"
import { voiceManager } from "./manager"

export const VoiceSettingsTab = observer(function VoiceSettingsTab({ store, onStart }: { store: CodeStore; onStart?: () => void }) {
    const [hasKey, setHasKey] = useState(false)
    const [voices, setVoices] = useState<string[]>([])
    const [key, setKey] = useState("")
    const [busy, setBusy] = useState(false)
    const [notice, setNotice] = useState("")
    const supported = hasVoiceBridge()
    useEffect(() => {
        if (!supported) return
        let alive = true
        void voiceAPI.status().then(
            (status) => {
                if (alive) {
                    setHasKey(status.hasKey)
                    setVoices(status.voices)
                }
            },
            (error: unknown) => {
                if (alive) setNotice(error instanceof Error ? error.message : "Could not read voice settings")
            }
        )
        return () => {
            alive = false
        }
    }, [supported])
    const updateKey = async (remove: boolean) => {
        setBusy(true)
        setNotice("")
        try {
            if (remove) {
                voiceManager.end()
                await voiceAPI.removeKey()
            } else await voiceAPI.setKey(key)
            setKey("")
            setHasKey(!remove)
            setNotice(remove ? "API key removed." : "API key saved securely. Start a conversation to verify GPT-Live access.")
        } catch (error) {
            setNotice(error instanceof Error ? error.message : "Could not update the voice key")
        } finally {
            setBusy(false)
        }
    }
    const voice = store.personalSettingsStore?.settings.current.voiceName ?? "gleam"
    return (
        <div className="space-y-5 p-5">
            <div>
                <h3 className="font-semibold">Conversation mode</h3>
                <p className="mt-2 text-sm text-muted">
                    Talk to one global assistant to manage tasks, run Actions, send messages, use Q-mode, and coordinate agents, processes, and follow-ups
                    across your computers.
                </p>
            </div>
            {!supported ? (
                <p className="border border-border p-3 text-sm">
                    Voice calls require the updated WildLark desktop app. Shared Admin controls remain available to text agents.
                </p>
            ) : (
                <>
                    <label className="block text-sm">
                        OpenAI API key{hasKey ? <span className="ml-2 text-primary">Saved</span> : null}
                        <input
                            type="password"
                            autoComplete="off"
                            spellCheck={false}
                            value={key}
                            onChange={(event) => setKey(event.target.value)}
                            placeholder={hasKey ? "Enter a replacement key" : "Enter your OpenAI API key"}
                            className="mt-2 block w-full border border-border bg-base-200 p-2"
                        />
                    </label>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={busy || !key.trim()}
                            className="btn border border-border px-3 py-2 disabled:opacity-40"
                            onClick={() => void updateKey(false)}
                        >
                            Save key
                        </button>
                        {hasKey ? (
                            <button type="button" disabled={busy} className="btn px-3 py-2 text-error disabled:opacity-40" onClick={() => void updateKey(true)}>
                                Remove key
                            </button>
                        ) : null}
                    </div>
                    <p className="text-xs text-muted">
                        The key is encrypted on this desktop. GPT-Live audio is billed to your OpenAI account; the controller uses your selected new-task agent
                        and its existing provider connection. Call recording storage is disabled.
                    </p>
                    <label className="block text-sm">
                        Voice
                        <select
                            value={voice}
                            onChange={(event) => store.personalSettingsStore?.settings.set({ voiceName: event.target.value })}
                            className="mt-2 block w-full border border-border bg-base-200 p-2"
                        >
                            {voices.map((name) => (
                                <option key={name} value={name}>
                                    {name[0].toUpperCase() + name.slice(1)}
                                </option>
                            ))}
                        </select>
                    </label>
                    <p className="text-sm text-muted">
                        Starting a call creates a visible Admin task with all current Admin capabilities. Its permissions can be changed in that task. Actions
                        keep their existing confirmations. Ending the call leaves accepted work running.
                    </p>
                    <button
                        type="button"
                        disabled={!hasKey || busy || store.isGuest}
                        className="btn bg-primary px-3 py-2 text-primary-content disabled:opacity-40"
                        onClick={() => {
                            void voiceManager.start(voice)
                            onStart?.()
                        }}
                    >
                        Start conversation
                    </button>
                </>
            )}
            {notice ? (
                <p role="status" className="text-sm">
                    {notice}
                </p>
            ) : null}
        </div>
    )
})
